import React, { useState, useMemo, useRef, useEffect } from 'react';
import './JumbledSentenceExercise.css';

function shuffleWithSeed(arr, seed) {
  const result = [...arr];
  let s = seed >>> 0;
  for (let i = result.length - 1; i > 0; i--) {
    s = ((s * 1664525) + 1013904223) >>> 0;
    const j = s % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  if (result.length > 1 && result.every((w, k) => w.uid === arr[k].uid)) {
    [result[0], result[1]] = [result[1], result[0]];
  }
  return result;
}

const ShuffleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
    <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
    <line x1="4" y1="4" x2="9" y2="9"/>
  </svg>
);

const JumbledSentenceExercise = ({
  sentences: initialSentences,
  title,
  level,
  onClose,
  updateAttributes,
  isStudent = false,
  isBoard = false,
  myState = {},
  otherState = {},
  updateState = () => {},
}) => {
  const initialTokens = useMemo(() => {
    return (initialSentences || []).map((s, idx) => {
      const words = s.sentence.trim().split(/\s+/).map((w, wi) => ({ word: w, uid: `${s.id}_${wi}` }));
      const shuffled = shuffleWithSeed(words, (idx + 1) * 31337 + s.id * 7);
      return { id: s.id, sentence: s.sentence.trim(), words, shuffled };
    });
  }, []); // eslint-disable-line

  const initBank = () => Object.fromEntries(initialTokens.map(t => [t.id, t.shuffled]));
  const initAnswer = () => Object.fromEntries(initialTokens.map(t => [t.id, []]));

  const [bankWords, setBankWords] = useState(initBank);
  const [answerWords, setAnswerWords] = useState(initAnswer);
  const [results, setResults] = useState({});
  const [checked, setChecked] = useState(false);
  const [score, setScore] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editableSentences, setEditableSentences] = useState(() => [...(initialSentences || [])]);
  const [editableTitle, setEditableTitle] = useState(title);

  const syncedRef = useRef(false);
  const prevResetAtRef = useRef(null);

  useEffect(() => {
    if (syncedRef.current) return;
    const src = Object.keys(myState).length > 0 ? myState
      : (Object.keys(otherState).length > 0 ? otherState : null);
    if (!src || (!src.answerWords && !src.results)) return;
    syncedRef.current = true;
    if (src.bankWords) setBankWords(src.bankWords);
    if (src.answerWords) setAnswerWords(src.answerWords);
    if (src.results) setResults(src.results);
    if (typeof src.checked === 'boolean') setChecked(src.checked);
    if (src.score) setScore(src.score);
  }, [myState, otherState]); // eslint-disable-line

  useEffect(() => {
    if (!otherState.answerWords) return;
    if (otherState.bankWords) setBankWords(otherState.bankWords);
    setAnswerWords(otherState.answerWords);
  }, [otherState.answerWords]); // eslint-disable-line

  useEffect(() => {
    if (!otherState.results) return;
    setResults(otherState.results);
    if (typeof otherState.checked === 'boolean') setChecked(otherState.checked);
    if (otherState.score) setScore(otherState.score);
  }, [otherState.results]); // eslint-disable-line

  useEffect(() => {
    if (otherState.resetAt && otherState.resetAt !== prevResetAtRef.current) {
      prevResetAtRef.current = otherState.resetAt;
      doReset(true);
    }
  }, [otherState.resetAt]); // eslint-disable-line

  const moveToAnswer = (sentenceId, uid) => {
    if (checked) return;
    const word = bankWords[sentenceId]?.find(w => w.uid === uid);
    if (!word) return;
    const nb = { ...bankWords, [sentenceId]: bankWords[sentenceId].filter(w => w.uid !== uid) };
    const na = { ...answerWords, [sentenceId]: [...answerWords[sentenceId], word] };
    setBankWords(nb);
    setAnswerWords(na);
    updateState({ bankWords: nb, answerWords: na });
  };

  const moveToBank = (sentenceId, uid) => {
    if (checked) return;
    const word = answerWords[sentenceId]?.find(w => w.uid === uid);
    if (!word) return;
    const nb = { ...bankWords, [sentenceId]: [...bankWords[sentenceId], word] };
    const na = { ...answerWords, [sentenceId]: answerWords[sentenceId].filter(w => w.uid !== uid) };
    setBankWords(nb);
    setAnswerWords(na);
    updateState({ bankWords: nb, answerWords: na });
  };

  const handleCheck = () => {
    const newResults = {};
    let correct = 0;
    initialTokens.forEach(t => {
      const answer = (answerWords[t.id] || []).map(w => w.word).join(' ');
      const isCorrect = answer === t.sentence;
      newResults[t.id] = isCorrect ? 'correct' : 'incorrect';
      if (isCorrect) correct++;
    });
    const scoreData = { correct, total: initialTokens.length };
    setResults(newResults);
    setChecked(true);
    setScore(scoreData);
    updateState({ bankWords, answerWords, results: newResults, checked: true, score: scoreData });
  };

  const doReset = (fromSync = false) => {
    const nb = initBank();
    const na = initAnswer();
    setBankWords(nb);
    setAnswerWords(na);
    setResults({});
    setChecked(false);
    setScore(null);
    if (!fromSync) {
      updateState({ bankWords: nb, answerWords: na, results: {}, checked: false, score: null, resetAt: Date.now() });
    }
  };

  const handleSaveEdit = () => {
    if (updateAttributes) {
      updateAttributes({ sentences: editableSentences, title: editableTitle, level });
    }
    setIsEditing(false);
  };

  const allAnswered = initialTokens.every(t => (answerWords[t.id] || []).length === t.words.length);

  // Results panel data — same ring/tier/headline pattern as the other
  // redesigned exercises, re-skinned with this exercise's sky accent.
  let resultsData = null;
  if (score) {
    const total = score.total || 1;
    const percent = Math.round((score.correct / total) * 100);
    const circumference = 2 * Math.PI * 42;
    const dashOffset = circumference * (1 - percent / 100);
    let tier = 'practice';
    let headline;
    let subline;
    if (percent >= 90) { tier = 'excellent'; headline = 'Outstanding!'; subline = isStudent ? 'You nailed it.' : 'Student nailed it.'; }
    else if (percent >= 70) { tier = 'great'; headline = 'Great job!'; subline = 'Solid performance.'; }
    else if (percent >= 50) { tier = 'good'; headline = 'Good work.'; subline = isStudent ? 'Keep going — you’re close.' : 'Getting there.'; }
    else { tier = 'practice'; headline = 'Keep practicing'; subline = isStudent ? 'You’ll get it — try again.' : 'Review and try again.'; }
    resultsData = { percent, circumference, dashOffset, tier, headline, subline };
  }

  return (
    <div className="js-exercise">
      {/* Header */}
      <div className="js-header">
        <div className="js-header-left">
          <div className="js-header-icon"><ShuffleIcon /></div>
          {isEditing ? (
            <input
              className="js-title-input"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
            />
          ) : (
            <span className="js-title">{title || 'Word Order Exercise'}</span>
          )}
          <span className="js-level-badge">{level || 'B1'}</span>
        </div>
        <div className="js-header-actions">
          {!isStudent && !isEditing && !checked && (
            <button className="js-icon-btn" title="Edit" onClick={() => setIsEditing(true)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
          )}
          <button className="js-icon-btn" title="Close" onClick={onClose}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="js-body">
        {/* Results panel */}
        {resultsData && (
          <div className={`js-results tier-${resultsData.tier}`}>
            <div className="js-results-ring-wrap">
              <svg viewBox="0 0 100 100" className="js-results-ring" aria-hidden>
                <circle cx="50" cy="50" r="42" className="js-results-ring-track" />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  className="js-results-ring-progress"
                  style={{ strokeDasharray: resultsData.circumference, strokeDashoffset: resultsData.dashOffset }}
                />
              </svg>
              <div className="js-results-ring-center">
                <div className="js-results-percent">{resultsData.percent}%</div>
              </div>
            </div>
            <div className="js-results-headline">{resultsData.headline}</div>
            <div className="js-results-subline">{resultsData.subline}</div>
            <div className="js-results-stats">
              <div className="js-results-stat is-correct">
                <span className="js-results-stat-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </span>
                <span className="js-results-stat-value">{score.correct}</span>
                <span className="js-results-stat-label">correct</span>
              </div>
              <div className="js-results-stats-divider" aria-hidden />
              <div className="js-results-stat is-incorrect">
                <span className="js-results-stat-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </span>
                <span className="js-results-stat-value">{score.total - score.correct}</span>
                <span className="js-results-stat-label">incorrect</span>
              </div>
            </div>
            <button className="js-results-cta" onClick={() => doReset()}>
              <span>Try Again</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>
          </div>
        )}

        {/* Edit mode */}
        {isEditing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {editableSentences.map((s, idx) => (
              <div key={s.id} className="js-edit-sentence-row">
                <span style={{ fontSize: 12, color: '#9ca3af', minWidth: 20 }}>{idx + 1}.</span>
                <input
                  className="js-edit-sentence-input"
                  value={s.sentence}
                  onChange={e => {
                    const updated = [...editableSentences];
                    updated[idx] = { ...s, sentence: e.target.value };
                    setEditableSentences(updated);
                  }}
                  placeholder="Enter sentence..."
                />
                <button
                  className="js-edit-remove-btn"
                  onClick={() => setEditableSentences(editableSentences.filter((_, i) => i !== idx))}
                >×</button>
              </div>
            ))}
            <button
              className="js-add-sentence-btn"
              onClick={() => setEditableSentences([...editableSentences, { id: Date.now(), sentence: '' }])}
            >+ Add sentence</button>
          </div>
        ) : (
          /* Play mode */
          initialTokens.map((t, idx) => {
            const result = results[t.id];
            const bank = bankWords[t.id] || [];
            const answer = answerWords[t.id] || [];
            return (
              <div key={t.id} className={`js-sentence-card ${result || ''}`}>
                <div className={`js-sentence-num ${result || ''}`}>
                  {result === 'correct' && <span className="js-sentence-result-icon">✓</span>}
                  {result === 'incorrect' && <span className="js-sentence-result-icon">✗</span>}
                  Sentence {idx + 1}
                </div>
                <div className="js-sentence-inner">
                  {/* Bank */}
                  <div>
                    <div className="js-zone-label">Word bank</div>
                    <div className={`js-word-zone ${bank.length === 0 ? 'empty' : ''}`}>
                      {bank.map(w => (
                        <span
                          key={w.uid}
                          className="js-chip"
                          onClick={() => moveToAnswer(t.id, w.uid)}
                        >{w.word}</span>
                      ))}
                    </div>
                  </div>
                  {/* Answer */}
                  <div>
                    <div className="js-zone-label">Your answer</div>
                    <div className={`js-word-zone answer-zone ${result || ''} ${answer.length === 0 ? 'empty' : ''}`}>
                      {answer.map(w => (
                        <span
                          key={w.uid}
                          className={`js-chip answer-chip ${result === 'correct' ? 'correct-chip' : result === 'incorrect' ? 'incorrect-chip' : ''}`}
                          onClick={() => moveToBank(t.id, w.uid)}
                        >{w.word}</span>
                      ))}
                    </div>
                  </div>
                  {/* Show correct answer if wrong */}
                  {result === 'incorrect' && (
                    <div className="js-correct-answer">
                      <strong>Correct: </strong>{t.sentence}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {(isEditing || !checked) && (
        <div className="js-footer">
          {isEditing ? (
            <>
              <button className="js-btn-cancel" onClick={() => { setIsEditing(false); setEditableSentences([...initialSentences]); setEditableTitle(title); }}>Cancel</button>
              <button className="js-btn-save" onClick={handleSaveEdit}>Save</button>
            </>
          ) : (
            <button className="js-btn-check" onClick={handleCheck} disabled={!allAnswered}>
              Check
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default JumbledSentenceExercise;
